import { useMemo, useEffect, useCallback } from 'react';
import { computeScore } from '../../domain/scoring';
import { useGame } from '../GameContext';
import { calculatePotential, calculateBankBlockedReason, calculateEndTurnBlockedReason } from '../../domain/potential';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import { debugLog } from '../debug/debugLog';
import { gameRules, makeEmptyDiceMask } from '../../domain/gameRules';
import { PASSAGE_ZONES, WIN_SCORE } from '../../utils/constants';


/**
 * Hook kapsułkujący logikę tury ONLINE (rzuty kośćmi, keep, potencjał, blokady bankowania).
 * Odciąża provider i ułatwia testowanie izolowane.
 */
export function useOnlineTurn() {
  const { gameState, rollDiceAction, toggleKeepDie, endTurn, isHost, currentClientPlayerId, rpcLock } = useGame();
  const turnCycle = gameState.turnCycle;
  const lastRoll = turnCycle?.lastRoll;
  const debugAdopt = typeof window !== 'undefined' && window.__DEBUG_ADOPT_ALL_PLAYERS__ === true;
  const rpcLocked = Boolean(rpcLock?.active);
  const isActivePlayer = useCallback((id?: string | null) => {
    if (!id) return false;
    const found = gameState.players.find((p) => p.id === id);
    return (found?.status || 'active') === 'active';
  }, [gameState.players]);

  const shortId = (id?: string | null) => (id ? id.slice(0, 8) : null);

  const potential = useMemo(() => calculatePotential(gameState), [gameState]);

  const hasRolledThisTurn = (turnCycle?.rollCount ?? 0) > 0;
  const effectiveLastRoll = hasRolledThisTurn ? lastRoll : undefined;
  const bust = effectiveLastRoll?.didBust ?? false;

  const rescueByFiveAvailable = effectiveLastRoll?.rescueAvailable ?? false;

  const hotDice = useMemo(() => {
    if (gameState.mode === 'REAL' || !turnCycle) return false;
    if (!effectiveLastRoll || !effectiveLastRoll.allScoring || effectiveLastRoll.dice.length === 0) return false;
    const score = computeScore(effectiveLastRoll.dice || []);
    const isStraight = score.basePoints === 150
      && score.scoringDiceCount === gameRules.dice.count
      && effectiveLastRoll.dice.length === gameRules.dice.count;
    if (isStraight) return false; // nie podpowiadamy hot-dice dla strita – gracz musi go dostrzec sam
    return effectiveLastRoll.dice.length === gameRules.dice.count;
  }, [effectiveLastRoll, gameState.mode, turnCycle]);

  const bankBlockedReason = useMemo(() => calculateBankBlockedReason(gameState, potential), [gameState, potential]);
  const endTurnBlockedReason = useMemo(() => calculateEndTurnBlockedReason(gameState, potential), [gameState, potential]);

  // Ekspozycja uproszczonego API dla komponentów prezentacyjnych
  const activePlayerId = turnCycle?.activePlayerId || null;
  const clientPlayer = gameState.players.find((p) => p.id === currentClientPlayerId);
  const clientIsActive = clientPlayer ? (clientPlayer.status || 'active') === 'active' : false;
  const effectiveClientId = debugAdopt && activePlayerId ? activePlayerId : (clientIsActive ? currentClientPlayerId : null);
  const isActiveTurn = !!activePlayerId && isActivePlayer(activePlayerId);
  const isClientTurnPlayer = !!activePlayerId && effectiveClientId === activePlayerId && isActivePlayer(activePlayerId);

  // Tunnel exit: how many points needed to leave the passage zone
  const tunnelNeed = useMemo(() => {
    const player = gameState.players.find((p) => p.id === activePlayerId);
    if (!player) return 0;
    const score = player.score ?? 0;
    const zone = PASSAGE_ZONES.find(z => score >= z.min && score < z.max);
    if (!zone) return 0;
    return zone.max - score;
  }, [gameState.players, activePlayerId]);

  // Over win score: potential would exceed WIN_SCORE
  const isOverWin = useMemo(() => {
    if (!potential) return false;
    const player = gameState.players.find((p) => p.id === activePlayerId);
    if (!player) return false;
    return (player.score ?? 0) + potential.points > WIN_SCORE;
  }, [potential, gameState.players, activePlayerId]);
  
  // Debug: mount/unmount hooka
  useEffect(() => {
    if (isVerboseLoggingEnabled()) {
      logger.debug('USE_ONLINE_TURN_MOUNT', {
        currentClientPlayerId: currentClientPlayerId?.slice(0, 8),
        gameMode: gameState?.mode,
      });
    }
    return () => {
      if (isVerboseLoggingEnabled()) {
        logger.debug('USE_ONLINE_TURN_UNMOUNT');
      }
    };
  }, [currentClientPlayerId, gameState?.mode]);
  
  // Debug: loguj zmiany kluczowych flag
  useEffect(() => {
    if (!isVerboseLoggingEnabled()) return;
    const summary = `turn=${gameState.turnNumber} active=${shortId(turnCycle?.activePlayerId) ?? '-'} client=${shortId(currentClientPlayerId) ?? '-'} status=${turnCycle?.status ?? 'N/A'} rollCount=${turnCycle?.rollCount ?? 0} bust=${bust} lastRollBust=${effectiveLastRoll?.didBust ?? false}`;
    logger.debug('TURN_STATE', {
      summary,
      activePlayerId: shortId(turnCycle?.activePlayerId),
      currentClientPlayerId: shortId(currentClientPlayerId),
      isActiveTurn,
      isClientTurnPlayer,
      bust,
      hotDice,
      rollCount: turnCycle?.rollCount,
      turnNumber: gameState.turnNumber,
      lastRollDidBust: effectiveLastRoll?.didBust ?? null,
      lastRollEvent: shortId(effectiveLastRoll?.eventId) ?? null,
      rpcLocked,
    });
  }, [turnCycle?.activePlayerId, currentClientPlayerId, isActiveTurn, isClientTurnPlayer, bust, hotDice, turnCycle?.rollCount, turnCycle?.status, gameState.turnNumber, effectiveLastRoll?.didBust, effectiveLastRoll?.eventId, rpcLocked]);

  useEffect(() => {
    if (!potential || gameState.mode === 'REAL') return;
    debugLog('POTENTIAL_UPDATED', {
      turn: gameState.turnNumber,
      playerId: turnCycle?.activePlayerId || null,
      rollCount: turnCycle?.rollCount ?? 0,
      dice: turnCycle?.dice || [],
      kept: turnCycle?.kept || makeEmptyDiceMask(),
      capturedMask: turnCycle?.capturedMask || makeEmptyDiceMask(),
      segments: gameState.segments || [],
      points: potential.points,
      scoringDice: potential.scoringDice,
    }, { source: 'useOnlineTurn', tags: ['potential'] });
  }, [
    gameState.mode,
    gameState.turnNumber,
    turnCycle?.activePlayerId,
    turnCycle?.rollCount,
    turnCycle?.dice,
    turnCycle?.kept,
    turnCycle?.capturedMask,
    gameState.segments,
    potential,
  ]);
  
  const keptMask = turnCycle?.kept || makeEmptyDiceMask();
  // Wymagamy jawnego keep ZAWSZE gdy poprzedni rzut miał punktujące kości (również allScoring)
  const requiresKeepBeforeRoll = !!effectiveLastRoll && !effectiveLastRoll.didBust && effectiveLastRoll.scoringDiceCount > 0;
  let hasKeptScoring = true;
  if (requiresKeepBeforeRoll && effectiveLastRoll && turnCycle) {
    const dice = turnCycle.dice || [];
    const newlyKept = (effectiveLastRoll.indices || []).filter((i: number) => keptMask[i]).map((i: number) => dice[i]);
    if (newlyKept.length === 0) {
      hasKeptScoring = false;
    } else {
      const sc = computeScore(newlyKept);
      hasKeptScoring = sc.basePoints > 0; // przynajmniej jedna punktująca kość
    }
  }
  const rollBlockedReason = (requiresKeepBeforeRoll && !hasKeptScoring) ? 'game.roll.keepRequired' : null;
  const hasNonScoringKept = !!(potential && potential.nonScoringKept && potential.nonScoringKept > 0);
  const rollBlockedDueToNonScoring = hasNonScoringKept ? 'game.roll.nonScoringKept' : null;
  const finalRollBlockedReason = rollBlockedReason || rollBlockedDueToNonScoring;
  const finalBankBlockedReason = bankBlockedReason;
  const finalEndTurnBlockedReason = endTurnBlockedReason;
  const canRoll = isClientTurnPlayer && isActiveTurn && !bust && (!requiresKeepBeforeRoll || hasKeptScoring) && !hasNonScoringKept && !rpcLocked;

  return useMemo(() => ({
    isHost,
    turnCycle,
    potential,
    rescueByFiveAvailable,
    bankBlockedReason: finalBankBlockedReason,
    endTurnBlockedReason: finalEndTurnBlockedReason,
    bust,
    hotDice,
    isActiveTurn,
    isClientTurnPlayer,
    canRoll,
    rollBlockedReason: finalRollBlockedReason,
    rollDice: rollDiceAction,
    toggleKeepDie,
    endTurn,
    rpcLocked,
    tunnelNeed,
    isOverWin,
  }), [
    isHost,
    turnCycle,
    potential,
    rescueByFiveAvailable,
    finalBankBlockedReason,
    finalEndTurnBlockedReason,
    bust,
    hotDice,
    isActiveTurn,
    isClientTurnPlayer,
    canRoll,
    finalRollBlockedReason,
    rollDiceAction,
    toggleKeepDie,
    endTurn,
    rpcLocked,
    tunnelNeed,
    isOverWin,
  ]);
}

export type UseOnlineTurnReturn = ReturnType<typeof useOnlineTurn>;
